import { useState } from "react";
import { categories, newsList, type NewsItem } from "@/pages/home/content";
import StatusBadge from "./StatusBadge";

export default function LatestFeed() {
  const [active, setActive] = useState("全部");
  const [liked, setLiked] = useState<Record<number, boolean>>({});
  const [openId, setOpenId] = useState<number | null>(null);

  const list = active === "全部" ? newsList : newsList.filter((n) => n.category === active);

  return (
    <section id="latest" className="w-full bg-background-50 py-12 md:py-16">
      <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-semibold tracking-[0.3em] text-primary-700">
              FRESH SCOOPS
            </p>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
              最新热瓜
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              按更新时间排序，每条均带核实状态，帮你快速判断“能不能先吃”。
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-1 rounded-full bg-background-100 px-1 py-1 md:mt-8">
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setActive(c)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                active === c
                  ? "bg-primary-500 font-medium text-background-50"
                  : "text-foreground-700 hover:bg-secondary-100"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 md:mt-8">
          {list.map((item) => (
            <ArticleCard
              key={item.id}
              item={item}
              liked={!!liked[item.id]}
              expanded={openId === item.id}
              onLike={() => setLiked((s) => ({ ...s, [item.id]: !s[item.id] }))}
              onToggle={() =>
                setOpenId((v) => (v === item.id ? null : item.id))
              }
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function ArticleCard({
  item,
  liked,
  expanded,
  onLike,
  onToggle,
}: {
  item: NewsItem;
  liked: boolean;
  expanded: boolean;
  onLike: () => void;
  onToggle: () => void;
}) {
  return (
    <article className="group flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-100/70">
      <div className="relative h-48 w-full overflow-hidden">
        <img
          src={item.image}
          alt={item.title}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 rounded-full bg-foreground-950/70 px-2.5 py-1 text-xs text-background-50 backdrop-blur">
          {item.category}
        </span>
        <div className="absolute bottom-3 left-3">
          <StatusBadge tone={item.status} />
        </div>
      </div>

      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">
          <button
            type="button"
            onClick={onToggle}
            className="cursor-pointer text-left transition-colors hover:text-primary-700"
          >
            {item.title}
          </button>
        </h3>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-700">
          {item.summary}
        </p>
        {expanded && (
          <p className="mt-2 animate-fadeUp text-sm leading-relaxed text-foreground-700">
            更多细节仍在多方求证中，为避免以讹传讹，我们会在获得可靠来源后持续补充更新，并更新对应核实状态。
          </p>
        )}

        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-600">
          <span className="whitespace-nowrap">
            <i className="ri-time-line mr-1" />
            {item.time}
          </span>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-eye-line" />
              {item.reads}
            </span>
            <button
              type="button"
              onClick={onLike}
              aria-label="点赞"
              className={`flex items-center gap-1 whitespace-nowrap transition-colors ${
                liked ? "text-primary-600" : "text-foreground-600 hover:text-primary-600"
              }`}
            >
              <i className={`${liked ? "ri-fire-fill" : "ri-fire-line"}`} />
              {liked ? (+item.likes + 1).toLocaleString() : item.likes}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}